import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { getAnalysisURL } from '@modules/bookmark/helpers';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { SaveArtefactConfirmDialog } from '@modules/common/components/SaveArtefact/SaveArtefactConfirmDialog';
import { NOTIFICATION_TIMEOUT_LONG_6S } from '@modules/common/constants';
import { OpenDashboardLink } from '@modules/dashboard/components/OpenDashboardLink';
import {
  getAnalysisTypeDisplayName,
  getDashboardLink,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { EditableDashboardListItem } from '@modules/dashboard/types';
import { AnalysisViewFragment, DashboardViewFragment } from 'generated/graphql';
import { useState } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { SaveFeedAndAddToDashboardForm } from '../SaveFeedAndAddToDashboardForm';

interface SaveFeedAndAddToDashboardDialogProps {
  isOpen: boolean;
  dashboardToAddTo?: EditableDashboardListItem;
  shouldAddToNewDashboard?: boolean;
  onClose: () => void;
  shouldSkipRedirect?: boolean;
  onCreated?: () => void;
}

export const SaveFeedAndAddToDashboardDialog = ({
  isOpen,
  dashboardToAddTo,
  shouldAddToNewDashboard,
  onClose,
  shouldSkipRedirect,
  onCreated,
}: SaveFeedAndAddToDashboardDialogProps) => {
  const [savedAnalysisRoute, setSavedAnalysisRoute] = useState<null | string>(
    null,
  );

  const { showSuccessNotification } = useNotificationContext();
  const useCase = useAnalysisStore(({ useCase }) => useCase);
  const analysisTypeDisplayName = getAnalysisTypeDisplayName(useCase);

  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const onAnalysisCreate = (
    analysis?: AnalysisViewFragment,
    dashboard?: Pick<DashboardViewFragment, 'title' | 'viewToken'>,
  ) => {
    onCreated?.();

    if (!analysis || !dashboard) {
      return;
    }
    showSuccessNotification({
      contentComponent: `${
        analysis?.title ?? `This ${analysisTypeDisplayName}`
      } is added to "${dashboard.title}"`,
      props: {
        isClosable: true,
        timeout: NOTIFICATION_TIMEOUT_LONG_6S,
        action: <OpenDashboardLink dashboard={dashboard} />,
      },
    });

    trackDashboardEvent({
      eventName: 'Analysis Added to Dashboard',
      dashboardLink: getDashboardLink(dashboard, tenantSlug),
      analysisLink: getAnalysisURL(analysis, tenantSlug),
      from: 'Analysis Page',
      useCase,
    });
  };

  return (
    <SaveArtefactConfirmDialog
      artefactRoute={savedAnalysisRoute}
      fullWidth
      maxWidth="sm"
      PaperProps={{ elevation: 3 }}
      open={isOpen}
      shouldSkipRedirect={shouldSkipRedirect}
    >
      <SaveFeedAndAddToDashboardForm
        dashboardToAddTo={dashboardToAddTo}
        setSavedAnalysisRoute={setSavedAnalysisRoute}
        onClose={onClose}
        shouldAddToNewDashboard={shouldAddToNewDashboard}
        onFeedCreate={onAnalysisCreate}
      />
    </SaveArtefactConfirmDialog>
  );
};
